import { EventMarker, EventWithRels } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { atomWithDebounce } from '@/atoms/debounce';
import { filtersAtom } from '@/atoms/filters/filter';
import { pageAndOrderParamsAtom } from '@/atoms/general';
import { comparisonIdAtom } from '@/atoms/queryParams';
import { EVENT_MARKERS_LIMIT } from '@/consts/visualisations';
import { teamMetricEventsUrl } from '@/query/url';
import { Nullable } from '@/types/generic';
import { NestedObject } from '@/types/object';
import { convertFilterParamsToString, hasEmptyInFilters } from '@/utils/api';
import { convertMarkerDataIntoMarkers, processEventDataAtom } from '@/utils/atoms/eventData';
import { fetchClientAtom } from '../client';

export const teamComparisonEventFilterDebounceObject = atomWithDebounce<Nullable<NestedObject>>(null);
const { debouncedValueAtom: teamComparisonEventFilterAtom } = teamComparisonEventFilterDebounceObject;

const rawTeamComparisonEventMarkersAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = [
    'teamComparisonEventMarkersForMetric',
    get(filtersAtom).metric,
    get(comparisonIdAtom),
    get(teamComparisonEventFilterAtom),
  ] as const;

  const queryFn = async ({ queryKey: [, metricKey, comparisonId, filterParams] }: { queryKey: typeof queryKey }) => {
    if (!comparisonId || !metricKey || hasEmptyInFilters(filterParams)) return [];
    return (await fetch(
      `/team/${comparisonId}/metric/${metricKey}/event-markers${convertFilterParamsToString({
        ...filterParams,
        limit: EVENT_MARKERS_LIMIT,
      })}`,
    )) as Promise<EventMarker[]>;
  };

  return { queryKey, queryFn };
});

export const teamComparisonEventMarkersForMetricAtom = atom(async get => {
  const data = await get(rawTeamComparisonEventMarkersAtom);
  return convertMarkerDataIntoMarkers(data.data);
});

const rawTeamComparisonEventsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = [
    'teamComparisonEventsForMetric',
    get(filtersAtom).metric,
    get(comparisonIdAtom),
    get(teamComparisonEventFilterAtom),
    get(pageAndOrderParamsAtom),
  ] as const;

  const queryFn = async ({
    queryKey: [, metricKey, comparisonId, filterParams, pageAndOrderParams],
  }: {
    queryKey: typeof queryKey;
  }) => {
    if (!comparisonId || !metricKey || hasEmptyInFilters(filterParams)) return [];
    const url = teamMetricEventsUrl(comparisonId, metricKey, {
      ...pageAndOrderParams,
      ...filterParams,
    });
    if (!url) return [];
    return (await fetch(url)) as Promise<EventWithRels[]>;
  };

  return { queryKey, queryFn };
});

export const teamComparisonEventsForMetricAtom = atom(async get =>
  processEventDataAtom(await get(rawTeamComparisonEventsAtom)),
);

const rawTeamComparisonEventsCountAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = [
    'teamComparisonEventsForMetricCount',
    get(filtersAtom).metric,
    get(comparisonIdAtom),
    get(teamComparisonEventFilterAtom),
  ] as const;

  const queryFn = async ({ queryKey: [, metricKey, comparisonId, filterParams] }: { queryKey: typeof queryKey }) => {
    if (!comparisonId || !metricKey || hasEmptyInFilters(filterParams)) return 0;
    return (await fetch(
      `/team/${comparisonId}/metric/${metricKey}/events/count${convertFilterParamsToString(filterParams)}`,
    )) as Promise<number>;
  };

  return { queryKey, queryFn };
});

export const teamComparisonEventsForMetricCountAtom = atom(
  async get => (await get(rawTeamComparisonEventsCountAtom)).data,
);
